import { useEffect, useRef } from "react";

/**
 * The aftrdrop invitation, matching ch-watcher's modal.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS REBUILT RATHER THAN SHARED
 * ─────────────────────────────────────────────────────────────────────────────
 * ch-watcher renders this with MUI `<Dialog>` against a black theme. This app
 * has no MUI at all — plain React over the `--ap-*` tokens in global.css, on a
 * light ground — so a shared component would mean importing MUI, emotion and a
 * dark palette into a codebase that deliberately has none of it. The two modals
 * say the same things in the same order; they are siblings, not one component
 * in two places. What must stay in step is the COPY and the LINKS, and those
 * are the only things worth diffing when either changes.
 *
 * A NATIVE <dialog>, not a div with role="dialog". showModal() gives focus
 * trapping, the inert backdrop and Escape-to-close from the platform, all of
 * which a hand-rolled overlay has to reimplement and usually half-implements.
 */

/** Where somebody applies for an aftrdrop invitation. Keep it in step with
 * ch-watcher's modal, which says the same things in the same order. */
const AFTRDROP_APPLY = "https://aftrdrop.app/apply";
const AFTRDROP_TESTFLIGHT = "https://testflight.apple.com/join/k8qjzWvs";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function DropNotificationsModal({ open, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // showModal() throws if called on an already-open dialog, and close() on an
    // already-closed one is a no-op that still fires nothing — so both are
    // guarded on the element's own state rather than on the prop.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="ap-modal"
      aria-labelledby="ap-modal-title"
      /* Escape closes the dialog natively; this keeps React's state in step so
         it can be reopened. Without it the dialog shuts and `open` stays true,
         and the next click on the trigger does nothing. */
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      /* Clicking the ::backdrop targets the dialog element itself; a click on
         anything inside targets a descendant. Comparing against the element is
         what separates "clicked outside" from "clicked the content". */
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="ap-modal__body">
        <img
          className="ap-modal__mark"
          src="/brand/aftrdrop-wordmark.svg"
          alt="aftrdrop"
          width={1592}
          height={335}
        />

        <h2 className="ap-modal__title" id="ap-modal-title">
          Drop Notifications
        </h2>

        <section className="ap-modal__section">
          <h3 className="ap-modal__heading">Exclusively for aftrdrop members</h3>
          <p className="ap-modal__copy">
            Drop notifications are available free of charge exclusively from the aftrdrop app.
          </p>
          <p className="ap-modal__copy">
            aftrdrop membership is free but requires an invitation. Apply on the site — it takes
            a couple of minutes.
          </p>
          <a
            className="ap-modal__link"
            href={AFTRDROP_APPLY}
            target="_blank"
            rel="noopener noreferrer"
          >
            {AFTRDROP_APPLY.replace("https://", "")}
          </a>
        </section>

        <section className="ap-modal__section">
          <h3 className="ap-modal__heading">aftrdrop iOS beta app</h3>
          <p className="ap-modal__copy">
            Once you've created an account, you can install the aftrdrop iOS beta app to receive
            drop notifications directly on your phone.
          </p>
          <a
            className="ap-modal__link"
            href={AFTRDROP_TESTFLIGHT}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get the app
          </a>
        </section>

        <button className="ap-modal__close" type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  );
}
